/**
 * work order service
 *
 * @version
 * 4.71.0 OAUI-684 Improve shared code and add interfaces
 * 4.67.0 OAUI-667 Add item progress to Work Orders
 * 4.67.0 OAUI-668 Remove usage of getCamductExportUrl
 * 4.63.0 DEVOPS-715 order-admin-ui: setup new repo without vertical slicing
 * 4.42.0 OAUI-554 Remove checks for deprecated app_vars (billing report, order-admin-ui v2)
 * 4.37.0 OAUI-527 Slowdowns in order modals
 * 4.35.0 OAUI-498 View Order Fittings and Products by Fulfillment Group
 * 4.34.0 OAUI-479 Optimize the list orders page to avoid overloaded SCSS files
 * 4.33.0 OAUI-481 The reports exports are no longer working
 * 4.33.0 OAUI-471 Add report/export links for orders to Work Orders V2
 * 4.33.0 OAUI-468 Remove duplicate calls for constants and hostConfiguration
 * 4.31.0 OAUI-455 Add basic table to display orders
 * 4.31.0 OAUI-452 Set up basic list orders routing
 * 4.26.0 OAUI-436 Change route for Order Management to use orderIndex
 * 4.26.0 OAUI-428 undefined error on certain orders
 * 4.26.0 OAUI-426 Add order management permissions
 * 4.26.0 OAUI-425 Add order items table for order management
 * 4.26.0 OAUI-424 Remove logoUrl, downloadUrl passing from order-admin-ui to order-api
 * 4.26.0 OAUI-420 Build Basic Order Management Layout
 * 4.26.0 OAUI-418 Hook up API calls for QR Code Order Management page
 * 4.26.0 OAUI-416 Add basic QR code order management route
 * 4.22.0 OAUI-390 Billing Report: build Angular UI
 *
 * @author Luis Hernandez <lhernandez@intersog.com>
 * @author Ryan Sweeney <rsweeney@buildcentrix.com>
 * @copyright (c) 2021-2024 Build Centrix
 */
import { Inject, Injectable } from '@angular/core';
import { ReportPayload } from '../../../types';
import { WdLocalEnvironmentService } from '../../environment/wd-local-environment.service';
import { WdLocalSharedFunctionsService } from '../../shared/wd-local-shared-functions.service';

@Injectable({
  providedIn: 'root',
})
export class WdWorkOrderReportService {
  /**
   * Constructor
   *
   * @since 4.26.0
   */
  constructor(
    public wdLocalSharedFunctions: WdLocalSharedFunctionsService,
    private wdEnvironmentService: WdLocalEnvironmentService,
    @Inject('wdErrorService') public wdErrorService: any,
    @Inject('wdOauth2HttpService') public wdOauth2HttpService: any,
    @Inject('wdSharedFunctionsService') public wdSharedFunctionsService: any,
  ) {}

  /**
   * GET Work Order Report by Index
   *
   * @since 4.26.0
   * @param index
   * @param reportType
   * @param opts
   */
  async getWorkOrderReportByIndex(
    index: string,
    reportType: string,
    opts: { format?: string; debug?: boolean },
  ): Promise<ReportPayload | undefined> {
    try {
      const url = this.buildReportUrl(index, {
        reportType,
        ...opts,
      });

      return (await this.wdOauth2HttpService.get(url)).data;
    } catch (error) {
      this.wdErrorService.userError(
        'Error',
        `Failed to load ${reportType} report`,
      );
      console.error(error);
      return undefined;
    }
  }

  /**
   * Get Report URL for an Order
   * @since 4.33.0
   */
  getOrderReportUrl(index: string, type: string, fgIndex?: string): string {
    // Params
    const opts: any = {
      // Add Access Token to Params
      access_token: this.wdOauth2HttpService.getAccessToken(),
    };

    // Add Fulfillment Group to Params (If Applicable)
    if (fgIndex) {
      opts.fulfillmentGroupIdx = fgIndex;
    }

    // Add Report Type to Params (If Applicable)
    if (type) {
      opts.reportType = type;
    }

    // Mapper for Export Type
    const REPORT_TYPE_MAPPER: { [key: string]: string } = {
      details: this.wdEnvironmentService.resources.getInternalOrderDetailsUrl(
        true,
        index,
      ),
      shopReportModern: this.wdEnvironmentService.resources.getWorkOrderShopReportModernUrl(
        index,
      ),
      billingReport: this.wdEnvironmentService.resources.getWorkOrderBillingReportUrl(
        index,
      ),
      supplyOnlyReport: this.wdEnvironmentService.resources.getWorkOrderSupplyOnlyReportUrl(
        index,
      ),
      fulfillment: this.buildReportUrl(index, opts),
      field: this.buildReportUrl(index, opts),
      csv: this.buildReportUrl(index, opts),
      cam: this.getCamExportUrl(index, opts),
    };

    return REPORT_TYPE_MAPPER[type];
  }

  /**
   * Build Report Url
   *
   * @since 4.26.0
   * @param orderId
   * @param opts
   */
  private buildReportUrl(
    orderId: string,
    opts: {
      reportType: string;
      format?: string;
      fulfillmentGroupIdx?: string;
      debug?: boolean;
      access_token?: string;
    },
  ): string {
    const params: Record<string, string> = {
      reportType: opts.reportType,
      idType: 'indexId',
    };
    if (opts.access_token) {
      params.access_token = opts.access_token;
    }
    if (opts.debug) {
      params.debug = 'true';
    }
    if (opts.fulfillmentGroupIdx) {
      params.fulfillmentGroupIdx = opts.fulfillmentGroupIdx;
    }
    if (opts.format) {
      params.format = opts.format;
    }

    return (
      `${this.wdLocalSharedFunctions.orderApiUrl}orders/reports/` +
      orderId +
      `?${new URLSearchParams(params)}`
    );
  }

  /**
   * Get the CAM export URL
   * @since 4.33.0
   */
  private getCamExportUrl(index: string, opts: any): string {
    // Cam Export API
    let camExportUrl =
      'Error: unable to generate URL for CAM export because the CAM system is missing or invalid.';

    // Camduct
    if (this.isCamductSystem()) {
      opts.exportType = 'camduct';
      opts.downloadContentType = 'text/html';
      camExportUrl =
        `${this.wdLocalSharedFunctions.publicApiUrl}orders/exports/` +
        index +
        `?${new URLSearchParams(opts)}`;
    }

    // Trimble
    if (this.isTrimbleVulcanSystem()) {
      opts.exportType = 'trimbleVulcan';
      opts.downloadContentType = 'application/zip';
      camExportUrl =
        `${this.wdLocalSharedFunctions.publicApiUrl}orders/exports/` +
        index +
        `?${new URLSearchParams(opts)}`;
    }

    // if the deployment's cam system is 'camduct'/'vulcan'
    // then use the public-api url to get the order export file
    return camExportUrl;
  }

  /**
   * Is Camduct System
   * - Checks if the Deployment's Default Cam System is CAMduct
   * @since 4.33.0
   */
  private isCamductSystem(): boolean {
    const camSystem = this.wdEnvironmentService.constants.getDefaultCamSystem;
    return typeof camSystem !== 'undefined' && camSystem.includes('camduct');
  }

  /**
   * Is Trimble Vulcan system
   * - Checks if the deployment's default CAM system is Trimble Vulcan
   * @since 4.33.0
   */
  private isTrimbleVulcanSystem(): boolean {
    const camSystem = this.wdEnvironmentService.constants.getDefaultCamSystem;
    return typeof camSystem !== 'undefined' && camSystem.includes('vulcan');
  }
}
